import { useState, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";

const MODOS = [
  { key: "base", label: "Normal", hint: "EBITDA base, sin ajustes" },
  { key: "ajustado", label: "Ajustado", hint: "ajustes del año" },
];

const MODOS_VALIDOS = new Set(MODOS.map((m) => m.key));

const storageKey = (empresaId) => `ebitda_modo_${empresaId || "global"}`;

/**
 * Hook: estado del switch de EBITDA, persistido por empresa en localStorage.
 * Devuelve [modo, setModo]. Por defecto "ajustado".
 * Cualquier valor guardado fuera de {base, ajustado} (p.ej. el antiguo
 * "multianio") se normaliza a "ajustado".
 */
export function useEbitdaModo(empresaId) {
  const [modo, setModoState] = useState("ajustado");

  useEffect(() => {
    if (!empresaId) return;
    const saved = localStorage.getItem(storageKey(empresaId));
    if (saved && MODOS_VALIDOS.has(saved)) setModoState(saved);
    else setModoState("ajustado");
  }, [empresaId]);

  const setModo = useCallback(
    (m) => {
      const valido = MODOS_VALIDOS.has(m) ? m : "ajustado";
      setModoState(valido);
      if (empresaId) localStorage.setItem(storageKey(empresaId), valido);
    },
    [empresaId]
  );

  return [modo, setModo];
}

/** Switch visual de 2 estados (Normal · Ajustado). */
export default function EbitdaModoSwitch({ modo, onChange }) {
  return (
    <div className="inline-flex rounded-lg border p-0.5 bg-muted/50" data-testid="ebitda-modo-switch">
      {MODOS.map((m) => (
        <button
          key={m.key}
          onClick={() => onChange(m.key)}
          title={m.hint}
          data-testid={`ebitda-modo-${m.key}`}
          className={cn(
            "px-3 py-1.5 text-sm rounded-md transition-colors",
            modo === m.key
              ? "bg-primary text-primary-foreground font-medium shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}
